'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/lib/auth';
import { MEETING_MANAGEMENT_ROLES } from '@/features/meeting-attendance/constants/roles';
import { meetingAttendanceApi } from '@/services/api/meeting-attendance';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Clock, TrendingUp, Users } from 'lucide-react';

export default function MeetingAttendanceDashboardPage() {
  const { isReady, user } = useAuthGuard();
  const { t } = useTranslation();
  const { hasAnyRole, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    todayMeetings: 0,
    activeMeetings: 0,
    totalUsers: 0,
    todayAttendance: 0,
  });

  const displayName = user?.displayName || user?.username || user?.email || '';
  const todayLabel = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    return formatter.format(new Date());
  }, []);
  const canManage = isAdmin || hasAnyRole(MEETING_MANAGEMENT_ROLES);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const [scheduled, inProgress, allMeetings, users] = await Promise.all([
        meetingAttendanceApi.listMeetings({ page: 1, limit: 1, status: 'SCHEDULED' }),
        meetingAttendanceApi.listMeetings({ page: 1, limit: 1, status: 'IN_PROGRESS' }),
        meetingAttendanceApi.listMeetings({ page: 1, limit: 500, status: 'all', anchor: 'now' }),
        meetingAttendanceApi.listUsers({ page: 1, limit: 1 }),
      ]);

      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(startOfDay);
      endOfDay.setDate(startOfDay.getDate() + 1);
      const isToday = (dateValue?: string | null) => {
        if (!dateValue) return false;
        const date = new Date(dateValue);
        return date >= startOfDay && date < endOfDay;
      };

      const meetingList = allMeetings?.meetings || [];
      const todayMeetings = meetingList.filter(
        (meeting: { startTime?: string; status?: string }) =>
          isToday(meeting.startTime) && ['SCHEDULED', 'IN_PROGRESS'].includes(meeting.status || ''),
      ).length;
      const todayAttendance = meetingList.reduce((total: number, meeting: { attendances?: Array<{ checkinTime?: string | null; status?: string }> }) => {
        if (!Array.isArray(meeting.attendances)) return total;
        const count = meeting.attendances.filter((attendance) => {
          if (!isToday(attendance.checkinTime)) return false;
          return ['ON_SITE', 'ONLINE', 'LATE'].includes(attendance.status || '');
        }).length;
        return total + count;
      }, 0);

      setStats({
        todayMeetings,
        activeMeetings: (scheduled?.pagination?.total || 0) + (inProgress?.pagination?.total || 0),
        totalUsers: users?.pagination?.total || 0,
        todayAttendance,
      });
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isReady) {
      fetchStats();
    }
  }, [fetchStats, isReady]);

  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <p className="text-gray-600">{t.meetingAttendance.common.loading}</p>
      </div>
    );
  }

  const statsCards = [
    {
      title: t.meetingAttendance.dashboard.todayMeetings,
      value: loading ? '--' : stats.todayMeetings,
      description: t.meetingAttendance.dashboard.todayMeetingsDesc,
      icon: Calendar,
      color: 'text-blue-600',
    },
    {
      title: t.meetingAttendance.dashboard.activeMeetings,
      value: loading ? '--' : stats.activeMeetings,
      description: t.meetingAttendance.dashboard.activeMeetingsDesc,
      icon: Clock,
      color: 'text-green-600',
    },
    {
      title: t.meetingAttendance.dashboard.totalUsers,
      value: loading ? '--' : stats.totalUsers,
      description: t.meetingAttendance.dashboard.totalUsersDesc,
      icon: Users,
      color: 'text-purple-600',
    },
    {
      title: t.meetingAttendance.dashboard.todayAttendance,
      value: loading ? '--' : stats.todayAttendance,
      description: t.meetingAttendance.dashboard.todayAttendanceDesc,
      icon: TrendingUp,
      color: 'text-orange-600',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">
          {t.meetingAttendance.dashboard.welcome.replace('{name}', displayName || t.meetingAttendance.dashboard.defaultUser)}
        </h1>
        <div className="text-sm text-gray-500">
          {t.meetingAttendance.dashboard.todayLabel.replace('{date}', todayLabel)}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                <Icon className={`h-4 w-4 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <CardDescription className="text-xs text-muted-foreground">
                  {stat.description}
                </CardDescription>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
          <CardTitle>{t.meetingAttendance.dashboard.quickActions}</CardTitle>
          <CardDescription>{t.meetingAttendance.dashboard.quickActionsHint}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {canManage && (
              <Link
                href="/meetingattendance/meetings/create"
                className="flex items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="text-center">
                  <Calendar className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <div className="text-sm font-medium">{t.meetingAttendance.dashboard.createMeeting}</div>
                </div>
              </Link>
            )}
            <Link
              href="/meetingattendance/meetings"
              className="flex items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
                <div className="text-center">
                  <Users className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <div className="text-sm font-medium">{t.meetingAttendance.dashboard.meetingList}</div>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t.meetingAttendance.dashboard.systemStatusTitle}</CardTitle>
            <CardDescription>{t.meetingAttendance.dashboard.systemStatusSubtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t.meetingAttendance.dashboard.systemStatusDatabase}</span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {t.meetingAttendance.dashboard.systemStatusNormal}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t.meetingAttendance.dashboard.systemStatusAuth}</span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {t.meetingAttendance.dashboard.systemStatusNormal}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t.meetingAttendance.dashboard.systemStatusQr}</span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {t.meetingAttendance.dashboard.systemStatusNormal}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
